function withEmoji<T extends { label: string; emoji: string }>(
  data: T[],
): Array<T & { labelEmoji: string }> {
  return data.map(({ emoji, label, ...rest }) => ({
    ...rest,
    label,
    emoji,
    labelEmoji: `${emoji} ${label}`,
  })) as Array<T & { labelEmoji: string }>;
}

export const COUNTRIES = withEmoji([
  { label: 'Argentina', value: 'AR', sampleId: '1', emoji: '🇦🇷' },
  { label: 'Australia', value: 'AU', sampleId: '26', emoji: '🇦🇺' },
  { label: 'Bolivia', value: 'BO', sampleId: '2', emoji: '🇧🇴' },
  { label: 'Brazil', value: 'BR', sampleId: '3', emoji: '🇧🇷' },
  { label: 'Canada', value: 'CA', externalPanel: true, sampleId: '5', emoji: '🇨🇦' },
  { label: 'Chile', value: 'CL', sampleId: '6', emoji: '🇨🇱' },
  { label: 'Colombia', value: 'CO', sampleId: '7', emoji: '🇨🇴' },
  { label: 'Costa Rica', value: 'CR', sampleId: '8', emoji: '🇨🇷' },
  { label: 'Dominican Republic ', value: 'DR', sampleId: '10', emoji: '🇩🇴' },
  { label: 'Ecuador', value: 'EC', sampleId: '11', emoji: '🇪🇨' },
  { label: 'El Salvador', value: 'SV', sampleId: '12', emoji: '🇸🇻' },
  { label: 'Finland', value: 'FI', externalPanel: true, emoji: '🇫🇮' },
  { label: 'France', value: 'FR', externalPanel: true, emoji: '🇫🇷' },
  { label: 'Guatemala', value: 'GT', sampleId: '13', emoji: '🇬🇹' },
  { label: 'Honduras', value: 'HN', sampleId: '14', emoji: '🇭🇳' },
  { label: 'Italy', value: 'IT', sampleId: '14', externalPanel: true, emoji: '🇮🇹' },
  { label: 'México', value: 'MX', sampleId: '15', emoji: '🇲🇽' },
  { label: 'Jamaica', value: 'JM', externalPanel: true, emoji: '🇯🇲' },
  { label: 'Nicaragua', value: 'NI', sampleId: '16', emoji: '🇳🇮' },
  { label: 'Norway', value: 'NO', externalPanel: true, emoji: '🇳🇴' },
  { label: 'Panama', value: 'PA', sampleId: '17', emoji: '🇵🇦' },
  { label: 'Paraguay', value: 'PY', sampleId: '18', emoji: '🇵🇾' },
  { label: 'Peru', value: 'PE', sampleId: '19', emoji: '🇵🇪' },
  { label: 'Philippines', value: 'PH', externalPanel: true, emoji: '🇵🇭' },
  { label: 'Puerto Rico', value: 'PR', sampleId: '20', emoji: '🇵🇷' },
  { label: 'South Korea', value: 'KR', externalPanel: true, emoji: '🇰🇷' },
  { label: 'Spain', value: 'ES', sampleId: '21', emoji: '🇪🇸' },
  { label: 'Sweden', value: 'SE', externalPanel: true, emoji: '🇸🇪' },
  { label: 'Taiwan', value: 'TW', externalPanel: true, emoji: '🇹🇼' },
  { label: 'Trinidad and Tobago', value: 'TT', externalPanel: true, emoji: '🇹🇹' },
  { label: 'United Kingdom', value: 'UK', externalPanel: true, emoji: '🇬🇧' },
  { label: 'United States', value: 'USA', sampleId: '22', emoji: '🇺🇸' },
  { label: 'Uruguay', value: 'UY', sampleId: '23', emoji: '🇺🇾' },
  { label: 'Venezuela', value: 'VE', sampleId: '24', emoji: '🇻🇪' },
]);

export const getCountryLabel = (
  code: string,
  { emoji = false }: { emoji?: boolean } = {},
): string => {
  const country = COUNTRIES.find(({ value }) => value === code);

  if (!country) return '#ERROR';

  return emoji ? country.labelEmoji : country.label;
};

interface InvoiceCountryConfig {
  label: string;

  percentageConfig: {
    required: boolean;
    name?: string;
    value?: string;
    addToTotal: boolean;
    customTotalCalculator?: (base: string, percentage: string) => number;
  };
}

export const invoiceCountriesConfig: Record<string, InvoiceCountryConfig> = {
  USA: {
    label: 'USA',

    percentageConfig: {
      required: false,
      addToTotal: true,
      name: 'WITHHOLDING',

      customTotalCalculator: (base, percentage) => {
        const FULL_PERCENTAGE = 100;

        const valueToIncrease = Number(percentage) || 0;

        const newBasePercentage = FULL_PERCENTAGE - valueToIncrease;

        const multiplier = FULL_PERCENTAGE / newBasePercentage;

        return Number(base) * multiplier;
      },
    },
  },

  BR: {
    label: 'USA',

    percentageConfig: {
      required: false,
      addToTotal: false,
    },
  },

  AR: {
    label: 'AR',

    percentageConfig: {
      required: true,
      addToTotal: true,
      value: '21.00',
      name: 'IVA',
    },
  },

  CL: {
    label: 'CL',

    percentageConfig: {
      required: true,
      addToTotal: true,
      value: '19.00',
      name: 'IVA',
    },
  },

  CO: {
    label: 'CO',

    percentageConfig: {
      required: true,
      addToTotal: true,
      value: '19.00',
      name: 'IVA',
    },
  },

  MX: {
    label: 'MX',

    percentageConfig: {
      required: true,
      addToTotal: true,
      value: '16.00',
      name: 'IVA',
    },
  },

  PE: {
    label: 'PE',

    percentageConfig: {
      required: true,
      addToTotal: true,
      value: '18.00',
      name: 'IGV',
    },
  },
};

export const INVOICE_COUNTRIES = COUNTRIES.filter(({ value }) =>
  ['USA', 'AR', 'BR', 'CL', 'CO', 'MX', 'PE'].includes(value),
);
